import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import type { ReactNode } from 'react'
import SeoHead from '../components/SeoHead'
import PricingCalculator from '../components/pricing/PricingCalculator'
import { orgSchema, breadcrumbSchema, faqPageSchema } from '../lib/seo'
import { LINKS } from '../lib/links'
import { buildStars } from '../lib/stars'
import { useT } from '../i18n/LocaleContext'
import { localizedPath } from '../i18n/routes'
import {
  PRICING_CONTENT,
  VOLUME_COLS,
  ENTRY_PRICE,
  HIGHEST_PRICE,
} from '../data/pricing'

/**
 * Page /tarifs — architecture « Le calculateur est roi », hero en SCÈNE NUIT
 * (même impact que le hero de la home) avec le calculateur serti dans la
 * bande sombre. Contenu strictement identique (contrat SEO). Ordre :
 * Estimer (nuit) → Comprendre → Croire → Marché → Services → FAQ → CTA
 * (la FAQ vit juste au-dessus du CTA, demande client).
 *
 * NB typo : les mots en dégradé sont des spans INLINE simples — l'ancien
 * inline-block + pb-[0.14em] créait un interligne inégal (ligne du dégradé
 * plus « longue » que les autres), défaut relevé par le client.
 */

const STARS_TARIFS = buildStars(46, 11, [0, 1], 5, 85)

/* Pictos maison des 5 services (mêmes tracés que la home, stroke 1.8) */
const SERVICE_ICONS: Record<string, ReactNode> = {
  'service.storage': (
    <>
      <path d="M3.5 9.5 12 4.5l8.5 5v9a1 1 0 0 1-1 1h-15a1 1 0 0 1-1-1z" />
      <path d="M8.5 19.5v-6h7v6" />
      <path d="M8.5 16.5h7" />
    </>
  ),
  'service.pickpack': (
    <>
      <path d="M4 8.2 12 4l8 4.2v7.6L12 20l-8-4.2z" />
      <path d="M4 8.2 12 12l8-3.8" />
      <path d="M12 12v8" />
    </>
  ),
  'service.shipping': (
    <>
      <path d="M3 7a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v9H3z" />
      <path d="M15 9h3.5a1 1 0 0 1 .82.43l1.5 2.14a1 1 0 0 1 .18.57V16h-6z" />
      <circle cx="7" cy="17.5" r="1.5" />
      <circle cx="17" cy="17.5" r="1.5" />
    </>
  ),
  'service.returns': (
    <>
      <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
      <path d="M21 4v4h-4" />
      <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
      <path d="M3 20v-4h4" />
    </>
  ),
  fulfillment: (
    <>
      <path d="M20.6 13.4 12 4.8H5.5A1.5 1.5 0 0 0 4 6.3v6.5l8.6 8.6a2 2 0 0 0 2.8 0l5.2-5.2a2 2 0 0 0 0-2.8z" />
      <circle cx="8.5" cy="9" r="1.3" />
    </>
  ),
}

const EASE = [0.22, 1, 0.36, 1] as const

/** Reveal de groupe : UNE animation par bloc. */
function Group({ children, className, delay = 0 }: { children: ReactNode; className?: string; delay?: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.7, delay, ease: EASE }}
      className={className}
    >
      {children}
    </motion.div>
  )
}

export default function TarifsPage() {
  const { t, locale } = useT()
  const content = PRICING_CONTENT[locale] ?? PRICING_CONTENT.fr
  const path = localizedPath('pricing', locale)
  const url = `https://yaslan.be${path}`

  return (
    <>
      <SeoHead
        title={content.seoTitle}
        description={content.metaDescription}
        path={path}
        jsonLd={[
          {
            '@type': 'Service',
            '@id': `${url}#service`,
            name: content.breadcrumb + ' — Yaslan',
            serviceType: 'Logistique e-commerce 3PL',
            description: content.metaDescription,
            provider: { '@id': 'https://yaslan.be/#organization' },
            areaServed: ['Europe', 'United Kingdom', 'Switzerland'],
            url,
            offers: {
              '@type': 'AggregateOffer',
              priceCurrency: 'EUR',
              lowPrice: ENTRY_PRICE.toFixed(2),
              highPrice: HIGHEST_PRICE.toFixed(2),
              eligibleQuantity: {
                '@type': 'QuantitativeValue',
                minValue: VOLUME_COLS[0].min,
                unitText: content.calcOrdersUnit,
              },
            },
          },
          faqPageSchema(content.faqs, { id: `${url}#faq`, inLanguage: locale }),
          orgSchema(locale),
          breadcrumbSchema([
            { name: t('breadcrumb.home'), path: localizedPath('home', locale) },
            { name: content.breadcrumb, path },
          ]),
        ]}
      />
      <main className="relative pb-24 overflow-hidden isolate">
        {/* ═══════════ ACTE 1 — ESTIMER : scène nuit, calculateur serti ═══════════ */}
        <section className="hero-night relative overflow-hidden bg-gradient-to-b from-ink-950 via-ink-900 to-ink-950 pt-28 sm:pt-32 pb-16 sm:pb-20">
          {/* Décor nuit : dot grid masqué + halo + étoiles (graine fixe) */}
          <div aria-hidden className="absolute inset-0 pointer-events-none">
            <div
              className="absolute inset-0 opacity-[0.14]"
              style={{
                backgroundImage: 'radial-gradient(rgba(255,255,255,0.18) 1px, transparent 1px)',
                backgroundSize: '28px 28px',
                maskImage: 'radial-gradient(ellipse at 50% 25%, black 25%, transparent 70%)',
              }}
            />
            <div
              className="absolute -top-40 left-[6%] w-[540px] h-[540px] rounded-full blur-3xl"
              style={{ background: 'radial-gradient(circle, rgba(59,93,255,0.24), transparent 70%)' }}
            />
            <div
              className="absolute top-[30%] right-[-8%] w-[480px] h-[480px] rounded-full blur-3xl"
              style={{ background: 'radial-gradient(circle, rgba(59,93,255,0.16), transparent 70%)' }}
            />
            <div className="absolute top-0 left-0 w-px h-px" style={{ boxShadow: STARS_TARIFS }} />
          </div>

          <div className="relative max-w-6xl mx-auto px-5 sm:px-6">
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-xs sm:text-sm text-ink-400"
            >
              <Link to={localizedPath('home', locale)} className="hover:text-accent-300 transition-colors">
                {t('breadcrumb.home')}
              </Link>
              <span className="mx-2 text-ink-600">/</span>
              <span className="text-ink-200">{content.breadcrumb}</span>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="mt-5 inline-flex items-center gap-2 rounded-full border border-accent-400/30 bg-accent-500/10 backdrop-blur-sm px-3 py-1 text-xs"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-accent-400 opacity-60 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-accent-400" />
              </span>
              <span className="font-medium text-accent-200">{content.badge}</span>
            </motion.div>

            {/* H1 — span dégradé INLINE simple : interligne parfaitement égal */}
            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
              className="mt-6 font-semibold tracking-[-0.03em] text-white text-[clamp(2.4rem,5.5vw,4rem)] leading-[1.08] max-w-3xl"
            >
              {content.titleLead}
              <span className="bg-gradient-to-r from-accent-300 via-accent-400 to-accent-500 bg-clip-text text-transparent [filter:drop-shadow(0_0_28px_rgba(59,93,255,0.4))]">
                {content.titleHighlight}
              </span>
              {content.titleTail}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.22 }}
              className="mt-5 text-lg text-ink-300 leading-relaxed max-w-2xl"
            >
              {content.intro}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.32 }}
              className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-3"
            >
              <a
                href="#calculateur"
                className="relative group inline-flex items-center gap-2 bg-accent-600 hover:bg-accent-500 text-white rounded-full pl-5 pr-4 py-3 text-sm font-medium shadow-[inset_0_1px_0_rgba(255,255,255,0.25),0_0_0_1px_rgba(94,132,255,0.4),0_16px_50px_-12px_rgba(59,93,255,0.8)] transition-colors overflow-hidden"
              >
                <span
                  aria-hidden
                  className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-[1100ms] ease-out bg-gradient-to-r from-transparent via-white/30 to-transparent pointer-events-none"
                />
                <span className="relative">{content.heroCtaCalc}</span>
                <span className="relative flex items-center justify-center w-5 h-5 rounded-full bg-white/20">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M12 5v14M5 13l7 7 7-7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              </a>
              <Link
                to={`${localizedPath('contact', locale)}#devis`}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent-300 hover:text-accent-200 transition-colors"
              >
                {content.heroCtaQuote}
                <span aria-hidden>→</span>
              </Link>
            </motion.div>

            <motion.ul
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="mt-8 pt-5 border-t border-white/10 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-ink-300"
            >
              {content.heroPoints.map((p) => (
                <li key={p} className="flex items-center gap-2">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" className="text-accent-400 shrink-0">
                    <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {p}
                </li>
              ))}
            </motion.ul>

            {/* Calculateur — l'instrument, serti dans la nuit */}
            <section id="calculateur" className="relative mt-14 sm:mt-16 scroll-mt-28">
              <motion.span
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="block text-[10px] sm:text-xs font-semibold tracking-[0.25em] uppercase text-accent-300"
              >
                {content.calcKicker}
              </motion.span>
              <motion.h2
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, ease: EASE }}
                className="mt-3 font-semibold tracking-[-0.03em] text-white text-[clamp(1.8rem,4vw,2.8rem)] leading-[1.1] max-w-3xl"
              >
                {content.calcTitle}
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="mt-4 text-ink-300 max-w-2xl leading-relaxed"
              >
                {content.calcSubtitle}
              </motion.p>

              <motion.p
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.15 }}
                className="mt-6 max-w-3xl flex items-start gap-3 rounded-2xl border border-accent-400/25 bg-accent-500/10 px-4 py-3.5 text-sm sm:text-base text-ink-200 leading-relaxed"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" className="mt-0.5 text-accent-300 shrink-0" aria-hidden>
                  <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span>{content.connectNote}</span>
              </motion.p>

              <Group className="mt-10" delay={0.1}>
                <PricingCalculator content={content} locale={locale} />
              </Group>
            </section>
          </div>
        </section>

        {/* ═══════════ ACTE 2 — COMPRENDRE : que facture Yaslan (bande blanche) ═══════════ */}
        <section className="border-b border-ink-950/5 bg-white py-20 sm:py-24">
          <div className="max-w-6xl mx-auto px-5 sm:px-6">
            <Group>
              <h2 className="font-semibold tracking-[-0.03em] text-ink-950 text-[clamp(1.7rem,3.4vw,2.4rem)] leading-[1.1] max-w-3xl">
                {content.billTitle}
              </h2>
              <p className="mt-4 text-ink-600 leading-relaxed max-w-3xl">{content.billText}</p>
            </Group>

            <Group
              delay={0.08}
              className="mt-10 grid lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-ink-950/5"
            >
              {[
                {
                  n: '01',
                  title: content.transportTitle,
                  desc: content.transportDesc,
                  points: content.transportPoints,
                  cls: 'lg:pr-12 pb-10 lg:pb-0',
                },
                {
                  n: '02',
                  title: content.pickpackTitle,
                  desc: content.pickpackDesc,
                  points: content.pickpackPoints,
                  cls: 'pt-10 lg:pt-0 lg:pl-12',
                },
              ].map((card) => (
                <div key={card.n} className={card.cls}>
                  <span className="font-semibold tracking-[-0.04em] leading-none text-[clamp(2rem,4vw,2.6rem)] bg-gradient-to-br from-ink-950 via-accent-700 to-accent-500 bg-clip-text text-transparent">
                    {card.n}
                  </span>
                  <h3 className="mt-3 text-xl sm:text-2xl font-semibold text-ink-950 tracking-tight">
                    {card.title}
                  </h3>
                  <p className="mt-2 text-sm sm:text-base text-ink-600 leading-relaxed">{card.desc}</p>
                  <ul className="mt-5 space-y-2.5">
                    {card.points.map((p) => (
                      <li key={p} className="flex items-start gap-2.5 text-sm text-ink-700">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" className="mt-0.5 text-accent-600 shrink-0">
                          <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        {p}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </Group>

            <Group delay={0.1} className="mt-12">
              <div className="rounded-[1.5rem] bg-ink-950/[0.04] p-1.5 ring-1 ring-ink-950/[0.06]">
                <div className="rounded-[calc(1.5rem-0.375rem)] border border-ink-950/[0.05] bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)] p-6 sm:p-8">
                  <h3 className="text-xl sm:text-2xl font-semibold text-ink-950 tracking-tight">
                    {content.notBilledTitle}
                  </h3>
                  <ul className="mt-5 grid sm:grid-cols-2 gap-x-8 gap-y-3">
                    {content.notBilledItems.map((item) => (
                      <li key={item} className="flex items-start gap-2.5 text-sm sm:text-base text-ink-700">
                        <span className="mt-0.5 flex items-center justify-center w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 shrink-0">
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6">
                            <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
                          </svg>
                        </span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Group>
          </div>
        </section>

        {/* ═══════════ ACTE 3 — CROIRE : l'histoire du fondateur ═══════════ */}
        <section className="py-20 sm:py-24">
          <div className="max-w-6xl mx-auto px-5 sm:px-6">
            <Group>
              <span className="text-[10px] sm:text-xs font-semibold tracking-[0.25em] uppercase text-accent-700">
                {content.founderKicker}
              </span>
              <h2 className="mt-4 font-semibold tracking-[-0.03em] text-ink-950 text-[clamp(1.7rem,3.4vw,2.4rem)] leading-[1.1] max-w-3xl">
                {content.founderTitleLead}
                <span className="bg-gradient-to-r from-accent-600 via-accent-500 to-accent-700 bg-clip-text text-transparent">
                  {content.founderTitleHighlight}
                </span>
                .
              </h2>
            </Group>

            {/* items-center : la citation est centrée face aux paragraphes,
                plus d'effet « collé au bord » */}
            <div className="mt-8 grid lg:grid-cols-[1.4fr_1fr] gap-8 lg:gap-12 items-center">
              <Group delay={0.05} className="space-y-5 text-ink-600 leading-relaxed">
                <p>{content.founderP1}</p>
                <p>{content.founderP2}</p>
                <Link
                  to={localizedPath('about', locale)}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent-700 hover:text-accent-800 transition-colors"
                >
                  {content.founderLink}
                  <span aria-hidden>→</span>
                </Link>
              </Group>

              <Group delay={0.12}>
                <blockquote className="relative rounded-3xl bg-gradient-to-br from-ink-950 via-ink-900 to-ink-950 ring-1 ring-white/10 text-white p-6 sm:p-8 overflow-hidden isolate">
                  <div
                    aria-hidden
                    className="absolute inset-0 opacity-25 -z-10"
                    style={{
                      backgroundImage: 'radial-gradient(rgba(255,255,255,0.16) 1px, transparent 1px)',
                      backgroundSize: '24px 24px',
                      maskImage: 'radial-gradient(ellipse at top right, black 20%, transparent 75%)',
                    }}
                  />
                  <span
                    aria-hidden
                    className="absolute -top-16 -right-16 w-48 h-48 rounded-full blur-3xl pointer-events-none -z-10"
                    style={{ background: 'radial-gradient(circle, rgba(59,93,255,0.4), transparent 70%)' }}
                  />
                  <div className="relative flex items-center gap-4">
                    <img
                      src="/founder.jpg"
                      alt={content.founderPhotoAlt}
                      width={96}
                      height={96}
                      loading="lazy"
                      className="w-20 h-20 sm:w-24 sm:h-24 rounded-full shrink-0 ring-2 ring-white/15"
                    />
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" className="text-accent-300 shrink-0">
                      <path d="M9.6 6C6.4 7.8 4.5 10.5 4.5 14c0 2.6 1.6 4.2 3.6 4.2 1.8 0 3.2-1.4 3.2-3.2 0-1.7-1.2-3-2.9-3-.3 0-.7 0-.8.1.3-1.8 1.8-3.7 3.5-4.7L9.6 6zm9 0c-3.2 1.8-5.1 4.5-5.1 8 0 2.6 1.6 4.2 3.6 4.2 1.8 0 3.2-1.4 3.2-3.2 0-1.7-1.2-3-2.9-3-.3 0-.6 0-.8.1.3-1.8 1.8-3.7 3.5-4.7L18.6 6z" />
                    </svg>
                  </div>
                  <p className="relative mt-5 text-lg sm:text-xl font-semibold tracking-tight leading-snug">
                    {content.founderQuote}
                  </p>
                </blockquote>
              </Group>
            </div>
          </div>
        </section>

        {/* ═══════════ Le marché (bande blanche, en-tête asymétrique centré) ═══════════ */}
        <section className="border-y border-ink-950/5 bg-white py-20 sm:py-24">
          <div className="max-w-6xl mx-auto px-5 sm:px-6">
            <Group className="grid lg:grid-cols-12 gap-x-12 gap-y-4 lg:items-center">
              <h2 className="lg:col-span-5 font-semibold tracking-[-0.03em] text-ink-950 text-[clamp(1.7rem,3.4vw,2.4rem)] leading-[1.1]">
                {content.costTitle}
              </h2>
              <div className="lg:col-span-7">
                <p className="text-ink-600 leading-relaxed">{content.costText}</p>
                <Link
                  to={localizedPath('blog.article', locale, { slug: content.guideSlug })}
                  className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-accent-700 hover:text-accent-800 transition-colors"
                >
                  {content.guideLabel}
                  <span aria-hidden>→</span>
                </Link>
              </div>
            </Group>
          </div>
        </section>

        {/* ═══════════ Services couverts ═══════════ */}
        <section className="py-20 sm:py-24">
          <div className="max-w-6xl mx-auto px-5 sm:px-6">
            <Group>
              <h2 className="font-semibold tracking-[-0.03em] text-ink-950 text-[clamp(1.6rem,3.2vw,2.2rem)] leading-[1.1] max-w-3xl">
                {content.linksTitle}
              </h2>
              <p className="mt-4 text-ink-600 max-w-2xl leading-relaxed">{content.linksSubtitle}</p>
            </Group>

            <Group delay={0.08} className="mt-8 border-y border-ink-950/5 divide-y divide-ink-950/5">
              {content.links.map((l) => (
                <Link
                  key={l.key}
                  to={localizedPath(l.key, locale)}
                  className="group grid grid-cols-[auto_1fr] sm:grid-cols-[auto_1fr_auto] items-center gap-x-5 gap-y-1.5 py-5 sm:py-6 px-2 -mx-2 rounded-xl hover:bg-ink-950/[0.02] transition-colors"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-accent-600 to-accent-700 text-white ring-1 ring-white/20 shadow-[0_10px_24px_-10px_rgba(59,93,255,0.7)]">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      {SERVICE_ICONS[l.key]}
                    </svg>
                  </span>
                  <span className="block">
                    <h3 className="text-base sm:text-lg font-semibold text-ink-950 tracking-tight">
                      {l.label}
                    </h3>
                    <span className="block mt-0.5 text-sm text-ink-600">{l.desc}</span>
                  </span>
                  <span className="col-start-2 sm:col-start-auto inline-flex items-center gap-1.5 text-sm font-semibold text-accent-700 group-hover:text-accent-800 transition-colors">
                    {content.linksCta}
                    <span aria-hidden className="transition-transform group-hover:translate-x-0.5">→</span>
                  </span>
                </Link>
              ))}
            </Group>
          </div>
        </section>

        {/* ═══════════ FAQ — juste au-dessus du CTA (demande client) ═══════════ */}
        <section className="border-y border-ink-950/5 bg-white py-20 sm:py-24">
          <div className="max-w-6xl mx-auto px-5 sm:px-6">
            <Group>
              <h2 className="font-semibold tracking-[-0.03em] text-ink-950 text-[clamp(1.7rem,3.4vw,2.4rem)] leading-[1.1] max-w-3xl">
                {content.faqTitle}
              </h2>
            </Group>
            <Group delay={0.08} className="mt-8 border-y border-ink-950/5 divide-y divide-ink-950/5">
              {content.faqs.map((f) => (
                <details key={f.q} className="group py-5 sm:py-6">
                  <summary className="flex items-start justify-between gap-4 cursor-pointer list-none">
                    <h3 className="text-base sm:text-lg font-semibold text-ink-950 tracking-tight">
                      {f.q}
                    </h3>
                    <span className="shrink-0 mt-0.5 w-6 h-6 rounded-full border border-ink-950/10 text-ink-500 flex items-center justify-center transition-[transform,border-color,color,background-color] duration-300 group-open:rotate-45 group-open:border-accent-300 group-open:bg-accent-50 group-open:text-accent-700">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M12 5v14M5 12h14" strokeLinecap="round" />
                      </svg>
                    </span>
                  </summary>
                  <p className="mt-3 text-sm sm:text-base text-ink-600 leading-relaxed max-w-3xl">{f.a}</p>
                </details>
              ))}
            </Group>
          </div>
        </section>

        {/* ═══════════ CTA final ═══════════ */}
        <section className="pt-20 sm:pt-24">
          <div className="max-w-6xl mx-auto px-5 sm:px-6">
            <Group>
              <div className="relative rounded-3xl bg-gradient-to-br from-ink-950 via-ink-900 to-ink-950 ring-1 ring-white/10 text-white px-6 sm:px-12 py-16 sm:py-20 overflow-hidden isolate">
                <div
                  aria-hidden
                  className="absolute inset-0 opacity-30 -z-10"
                  style={{
                    backgroundImage: 'radial-gradient(rgba(255,255,255,0.18) 1px, transparent 1px)',
                    backgroundSize: '28px 28px',
                    maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 75%)',
                  }}
                />
                <motion.div
                  aria-hidden
                  animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.8, 0.5] }}
                  transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
                  className="absolute -right-32 -top-32 w-[460px] h-[460px] rounded-full blur-3xl -z-10"
                  style={{ background: 'radial-gradient(circle, rgba(59,93,255,0.55), transparent 70%)' }}
                />

                <div className="relative max-w-3xl">
                  <span className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 backdrop-blur-sm px-3 py-1 text-xs font-semibold text-accent-200">
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-accent-400 opacity-70 animate-ping" />
                      <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-accent-400" />
                    </span>
                    {content.ctaBadge}
                  </span>

                  <h2 className="mt-6 font-semibold tracking-[-0.035em] text-[clamp(2.2rem,5.5vw,4rem)] leading-[1.06]">
                    {content.ctaTitleLead}
                    <span className="bg-gradient-to-r from-accent-300 via-accent-200 to-white bg-clip-text text-transparent">
                      {content.ctaTitleHighlight}
                    </span>
                    .
                  </h2>

                  <p className="mt-5 sm:mt-6 text-base sm:text-lg text-ink-300 leading-relaxed max-w-xl">
                    {content.ctaCopy}
                  </p>

                  <div className="mt-8 sm:mt-10 flex flex-wrap items-center gap-3 sm:gap-4">
                    <Link
                      to={`${localizedPath('contact', locale)}#devis`}
                      className="relative group inline-flex items-center gap-2.5 bg-ink-100 hover:bg-accent-50 text-ink-950 rounded-full pl-6 pr-5 py-3.5 sm:py-4 text-sm sm:text-base font-semibold shadow-[0_24px_60px_-18px_rgba(59,93,255,0.7)] transition-colors overflow-hidden"
                    >
                      <span
                        aria-hidden
                        className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-[1100ms] ease-out bg-gradient-to-r from-transparent via-accent-200/60 to-transparent pointer-events-none"
                      />
                      <span className="relative">{content.ctaPrimary}</span>
                      <span className="relative flex items-center justify-center w-6 h-6 rounded-full bg-ink-950 text-white">
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                    </Link>
                    <Link
                      to={`${localizedPath('contact', locale)}#rendez-vous`}
                      className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 backdrop-blur px-5 py-3.5 sm:py-4 text-sm sm:text-base font-medium text-white/90 hover:bg-white/10 hover:border-white/30 transition-colors"
                    >
                      {content.ctaSecondary}
                    </Link>
                  </div>

                  <div className="mt-10 sm:mt-12 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs sm:text-sm text-ink-400">
                    <span className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                      {content.trust1}
                    </span>
                    <span className="flex items-center gap-2">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" className="text-accent-400">
                        <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      {content.trust2}
                    </span>
                    <span className="flex items-center gap-2">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" className="text-accent-400">
                        <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      {content.trust3}
                    </span>
                  </div>
                </div>
              </div>
            </Group>
          </div>
        </section>
      </main>
    </>
  )
}
